const apiBase = import.meta.env.VITE_API_URL?.replace(/\/$/, "") ?? "";

export interface ApiEnvelope<T> {
  data: T;
  meta: { requestId: string; [key: string]: unknown };
  error: null;
}

interface ApiFailure {
  data: null;
  meta: { requestId: string };
  error: { code: string; message: string };
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly requestId?: string,
  ) {
    super(message);
  }
}

export type AccessMode = "admin" | "client";

export interface Session {
  token: string;
  expiresAt: number;
  user: {
    id: string;
    name: string;
    email: string;
    mode: AccessMode;
    clientId?: string;
  };
  impersonation?: { clientId: string; clientName: string };
  returnSession?: Session;
}

export const dashboardSessionStorageKey = "qassist.dashboard.session";
export const dashboardSessionInvalidatedEvent = "qassist:dashboard-session-invalidated";

type DashboardSessionStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function loadSession(
  persistentStorage: DashboardSessionStorage = localStorage,
  legacyTabStorage: DashboardSessionStorage = sessionStorage,
): Session | null {
  try {
    const value =
      persistentStorage.getItem(dashboardSessionStorageKey) ??
      legacyTabStorage.getItem(dashboardSessionStorageKey);
    if (!value) return null;
    const parsed = JSON.parse(value) as Session;
    if (!parsed.token || parsed.expiresAt <= Date.now()) {
      persistentStorage.removeItem(dashboardSessionStorageKey);
      legacyTabStorage.removeItem(dashboardSessionStorageKey);
      return null;
    }
    persistentStorage.setItem(dashboardSessionStorageKey, value);
    legacyTabStorage.removeItem(dashboardSessionStorageKey);
    return parsed;
  } catch {
    persistentStorage.removeItem(dashboardSessionStorageKey);
    legacyTabStorage.removeItem(dashboardSessionStorageKey);
    return null;
  }
}

export function saveSession(
  session: Session | null,
  persistentStorage: DashboardSessionStorage = localStorage,
  legacyTabStorage: DashboardSessionStorage = sessionStorage,
): void {
  if (session) persistentStorage.setItem(dashboardSessionStorageKey, JSON.stringify(session));
  else persistentStorage.removeItem(dashboardSessionStorageKey);
  legacyTabStorage.removeItem(dashboardSessionStorageKey);
}

export function shouldInvalidateDashboardSession(code: string, mode: AccessMode): boolean {
  return (
    code === "UNAUTHENTICATED" ||
    (code === "ADMIN_REQUIRED" && mode === "admin") ||
    (code === "CLIENT_REQUIRED" && mode === "client")
  );
}

function invalidateRejectedDashboardSession(code: string, token: string | undefined): void {
  if (!token) return;
  const session = loadSession();
  if (
    !session ||
    session.token !== token ||
    !shouldInvalidateDashboardSession(code, session.user.mode)
  )
    return;
  saveSession(null);
  window.dispatchEvent(new Event(dashboardSessionInvalidatedEvent));
}

export async function api<T>(path: string, options: RequestInit = {}, token?: string): Promise<T> {
  const resolvedToken = token ?? loadSession()?.token;
  const headers = new Headers(options.headers);
  if (resolvedToken) headers.set("Authorization", `Bearer ${resolvedToken}`);
  if (options.body && !(options.body instanceof ArrayBuffer) && !headers.has("Content-Type"))
    headers.set("Content-Type", "application/json");
  const response = await fetch(`${apiBase}${path}`, { ...options, headers });
  const responseText = await response.text();
  let payload: ApiEnvelope<T> | ApiFailure;
  try {
    payload = JSON.parse(responseText) as ApiEnvelope<T> | ApiFailure;
  } catch {
    const requestId = response.headers.get("X-Request-ID") ?? undefined;
    if (response.status === 413)
      throw new ApiError(
        "The upload is too large for the public proxy. Choose a smaller file or raise the proxy upload limit.",
        "UPLOAD_TOO_LARGE",
        requestId,
      );
    throw new ApiError(
      response.ok
        ? "The server returned an unreadable response. Please try again."
        : `The server could not complete the request (status ${response.status}). Please try again.`,
      "INVALID_SERVER_RESPONSE",
      requestId,
    );
  }
  if (!response.ok || payload.error) {
    const errorCode = payload.error?.code ?? "REQUEST_FAILED";
    invalidateRejectedDashboardSession(errorCode, resolvedToken);
    throw new ApiError(
      payload.error?.message ?? `Request failed with status ${response.status}`,
      errorCode,
      payload.meta?.requestId,
    );
  }
  return payload.data;
}

export async function login(email: string, password: string, mode: AccessMode): Promise<Session> {
  return api<Session>("/api/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password, mode }),
  });
}

export async function uploadAsset(file: File): Promise<unknown> {
  return api("/api/v1/assets/upload", {
    method: "POST",
    headers: {
      "Content-Type": file.type,
      "X-File-Name-Encoded": encodeURIComponent(file.name),
    },
    body: await file.arrayBuffer(),
  });
}

export async function uploadPlatformAsset<T>(file: File): Promise<T> {
  const contentType = /\.mp3$/i.test(file.name) ? "audio/mpeg" : file.type;
  return api<T>("/api/v1/admin/platform-assets/upload", {
    method: "POST",
    headers: {
      "Content-Type": contentType,
      "X-File-Name-Encoded": encodeURIComponent(file.name),
    },
    body: await file.arrayBuffer(),
  });
}

interface MultipartUploadStart {
  sessionId: string;
  chunkBytes: number;
  chunkCount: number;
}

interface MultipartUploadPart {
  partNumber: number;
  etag: string;
}

function uploadPlatformAssetPart(
  sessionId: string,
  partNumber: number,
  body: Blob,
  completedBytes: number,
  totalBytes: number,
  onProgress: (percent: number) => void,
): Promise<MultipartUploadPart> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(
      "PUT",
      `${apiBase}/api/v1/admin/platform-assets/multipart/${sessionId}/parts/${partNumber}`,
    );
    const token = loadSession()?.token;
    if (token) request.setRequestHeader("Authorization", `Bearer ${token}`);
    request.setRequestHeader("Content-Type", "application/octet-stream");
    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable)
        onProgress(Math.min(99, Math.round(((completedBytes + event.loaded) / totalBytes) * 100)));
    });
    request.addEventListener("error", () =>
      reject(new ApiError("The upload could not reach the server.", "UPLOAD_NETWORK_ERROR")),
    );
    request.addEventListener("abort", () =>
      reject(new ApiError("The upload was cancelled.", "UPLOAD_CANCELLED")),
    );
    request.addEventListener("load", () => {
      let payload: ApiEnvelope<MultipartUploadPart> | ApiFailure;
      try {
        payload = JSON.parse(request.responseText) as ApiEnvelope<MultipartUploadPart> | ApiFailure;
      } catch {
        reject(
          new ApiError(
            request.status === 413
              ? "An upload part is too large for the public proxy."
              : `The server could not complete the upload (status ${request.status}).`,
            request.status === 413 ? "UPLOAD_PART_TOO_LARGE" : "INVALID_SERVER_RESPONSE",
            request.getResponseHeader("X-Request-ID") ?? undefined,
          ),
        );
        return;
      }
      if (request.status < 200 || request.status >= 300 || payload.error) {
        reject(
          new ApiError(
            payload.error?.message ?? `Upload failed with status ${request.status}`,
            payload.error?.code ?? "UPLOAD_FAILED",
            payload.meta?.requestId,
          ),
        );
        return;
      }
      resolve(payload.data);
    });
    request.send(body);
  });
}

async function uploadLargePlatformAsset<T>(
  file: File,
  onProgress: (percent: number) => void,
): Promise<T> {
  const contentType = /\.mp3$/i.test(file.name) ? "audio/mpeg" : file.type;
  const started = await api<MultipartUploadStart>("/api/v1/admin/platform-assets/multipart", {
    method: "POST",
    body: JSON.stringify({ fileName: file.name, contentType, sizeBytes: file.size }),
  });
  const parts: MultipartUploadPart[] = [];
  try {
    for (let index = 0; index < started.chunkCount; index += 1) {
      const start = index * started.chunkBytes;
      const end = Math.min(file.size, start + started.chunkBytes);
      parts.push(
        await uploadPlatformAssetPart(
          started.sessionId,
          index + 1,
          file.slice(start, end),
          start,
          file.size,
          onProgress,
        ),
      );
    }
    const asset = await api<T>(
      `/api/v1/admin/platform-assets/multipart/${started.sessionId}/complete`,
      {
        method: "POST",
        body: JSON.stringify({ parts }),
      },
    );
    onProgress(100);
    return asset;
  } catch (error) {
    await api(`/api/v1/admin/platform-assets/multipart/${started.sessionId}`, {
      method: "DELETE",
    }).catch(() => undefined);
    throw error;
  }
}

export function uploadAssetWithProgress<T>(
  file: File,
  mode: AccessMode,
  onProgress: (percent: number) => void,
): Promise<T> {
  if (mode === "admin" && file.size > 64 * 1024 * 1024)
    return uploadLargePlatformAsset<T>(file, onProgress);
  return new Promise<T>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(
      "POST",
      `${apiBase}${
        mode === "admin" ? "/api/v1/admin/platform-assets/upload" : "/api/v1/assets/upload"
      }`,
    );
    const token = loadSession()?.token;
    if (token) request.setRequestHeader("Authorization", `Bearer ${token}`);
    request.setRequestHeader(
      "Content-Type",
      mode === "admin" && /\.mp3$/i.test(file.name) ? "audio/mpeg" : file.type,
    );
    request.setRequestHeader("X-File-Name-Encoded", encodeURIComponent(file.name));
    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable)
        onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
    });
    request.addEventListener("error", () =>
      reject(new ApiError("The upload could not reach the server.", "UPLOAD_NETWORK_ERROR")),
    );
    request.addEventListener("abort", () =>
      reject(new ApiError("The upload was cancelled.", "UPLOAD_CANCELLED")),
    );
    request.addEventListener("load", () => {
      let payload: ApiEnvelope<T> | ApiFailure;
      try {
        payload = JSON.parse(request.responseText) as ApiEnvelope<T> | ApiFailure;
      } catch {
        reject(
          new ApiError(
            request.status === 413
              ? "The upload is too large for the public proxy. Choose a smaller file or raise the proxy upload limit."
              : `The server could not complete the upload (status ${request.status}).`,
            request.status === 413 ? "UPLOAD_TOO_LARGE" : "INVALID_SERVER_RESPONSE",
            request.getResponseHeader("X-Request-ID") ?? undefined,
          ),
        );
        return;
      }
      if (request.status < 200 || request.status >= 300 || payload.error) {
        reject(
          new ApiError(
            payload.error?.message ?? `Upload failed with status ${request.status}`,
            payload.error?.code ?? "UPLOAD_FAILED",
            payload.meta?.requestId,
          ),
        );
        return;
      }
      onProgress(100);
      resolve(payload.data);
    });
    request.send(file);
  });
}

export async function prepareSystemBackupDownload(jobId: string): Promise<string> {
  const result = await api<{ url: string }>(
    `/api/v1/admin/system-backup/${jobId}/download-ticket`,
    { method: "POST" },
  );
  return `${apiBase}${result.url}`;
}

export const playerApiBase = apiBase;
